import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Alan, Buton, GirisKabugu, NotKutusu, OnayKutusu, SifreGucu } from "@koclukpanel/ui";
import {
  ApiHatasi,
  davetOnayMetinleri,
  davetiTamamla,
  sifreSifirla,
  type DavetDurumu,
} from "./api";
import {
  AKTIVASYON_RIZA_KODLARI,
  aktivasyonOnaylari,
  aktivasyonOnaylariTamMi,
  onayAnahtari,
  DAVET_GUN,
  SIFRE_SIFIRLAMA_DAKIKA,
  AKIS_METNI,
  GUC_ADI,
  MUVAFAKAT_BEKLIYOR_METNI,
  PERSONEL_EN_AZ,
  jetonGecerliGorunuyorMu,
  jetonHatasiniSinifla,
  sifreDogrula,
  sifreGucu,
  type JetonSayfasi as Sayfa,
  type SifreFormu,
} from "@koclukpanel/contracts";

/**
 * DAVET / ŞİFRE SIFIRLAMA SAYFASI — F-PPL-01b
 *
 * ================================================================
 * BU SAYFA OLMADAN PERSONEL EKLENEMEZDİ
 * ================================================================
 * `POST /auth/daveti-tamamla` sunucuda vardı, davet e-postası da
 * gidiyordu — ama bağlantının açtığı sayfa hiç yazılmamıştı. Yani
 * bir personel hesabı açılsa bile o kişi şifresini belirleyemiyor,
 * hesabına hiç giremiyordu. Aynısı şifre sıfırlama için de geçerliydi.
 *
 * ================================================================
 * İKİ AKIŞ TEK BİLEŞEN
 * ================================================================
 * Davet ve sıfırlama yalnızca çağırdıkları uçta ve metinlerinde
 * ayrışıyor; ikisi de "jetonla şifre belirle". Ayrı iki bileşen
 * yazmak, jeton doğrulaması ve hata gösterimi gibi hassas kısımların
 * iki kopyasını üretirdi.
 *
 * ================================================================
 * KABUK ARTIK GİRİŞ EKRANIYLA AYNI — `GirisKabugu`
 * ================================================================
 * Çizim (`DavetKabul.dc.html` · `SifreSifirlama.dc.html` ·
 * `JetonGecersiz.dc.html`) üç ekranı da giriş ekranının iki sütunlu
 * kabuğunda gösteriyor: solda yaşayan hafta, sağda 396px'lik sütun.
 * Önceden ortada dar bir `Kart` çiziliyordu.
 *
 * Fark kozmetik değil: kullanıcı bu sayfaya E-POSTADAN geliyor ve
 * gördüğü ilk şey "doğru yere mi geldim" sorusunun cevabı olmalı.
 * Giriş ekranıyla aynı kabuk, o soruyu tek bakışta kapatıyor.
 *
 * ================================================================
 * ŞİFRE POLİTİKASI BURADA YAŞAMIYOR
 * ================================================================
 * Uzunluk ve karmaşıklık kuralları `password.ts` içinde. Ekran
 * yalnızca en az uzunluğu ve "yaygın şifre reddedilir" kuralını
 * yazıyor — ikisi de çizimin kural kutusunda var. Karakter sınıfı
 * listesi BİLEREK kopyalanmadı: kabul kararını sunucu veriyor ve
 * `ayrinti.sifre` cevabı zaten tam olarak neyin eksik olduğunu
 * söylüyor.
 */
export function JetonSayfasi({ sayfa }: { sayfa: Sayfa }) {
  const [form, setForm] = useState<SifreFormu>({ sifre: "", tekrar: "" });
  const [denendi, setDenendi] = useState(false);
  const [bitti, setBitti] = useState(false);
  const [muvafakatBekliyor, setMuvafakatBekliyor] = useState(false);

  const metin = AKIS_METNI[sayfa.akis];
  const hatalar = sifreDogrula(form);
  const gecerli = Object.keys(hatalar).length === 0;
  const jetonTamam = jetonGecerliGorunuyorMu(sayfa.jeton);
  /*
    PANEL PERSONEL EKRANI — eşik `PERSONEL_EN_AZ`. Öğrenci uygulamasına
    taşınırsa `OGRENCI_EN_AZ` geçilmeli; parametre zorunlu olduğu için
    unutmak derleme hatası veriyor.
  */
  const guc = sifreGucu(form.sifre, PERSONEL_EN_AZ);

  /*
    ================================================================
    AKTIVASYONDA ZORUNLU ONAY — F-LGL-03a (Emre, 24.10.2026)
    ================================================================
    "Hesap açılırken zorunlu metinlerin onay kutusu gelir, onaylamadan aktivasyon tamamlanmaz." Hangi metinlerin
    istendiğine SUNUCU karar verir; ekran yaş hesaplamaz, tür listesi tutmaz. Liste boşsa (18 yaş altı ya da
    yayında metin yok) hiç kutu çizilmez. Kutular işaretlenmeden gönder pasif — ama asıl kapı sunucuda: ekranı
    atlayan bir istek de reddedilir. Onay, kullanıcının GÖRDÜĞÜ metin sürümüyle gider.
  */
  const davetMi = sayfa.akis === "davet";
  const metinSorgu = useQuery({
    queryKey: ["davet-onay-metinleri", sayfa.jeton],
    queryFn: () => davetOnayMetinleri(sayfa.jeton),
    enabled: davetMi && jetonTamam,
    retry: false,
  });
  const metinler = metinSorgu.data?.metinler ?? [];
  const [isaretli, setIsaretli] = useState<Record<string, boolean>>({});
  const onayHazir =
    !davetMi || (metinSorgu.isSuccess && aktivasyonOnaylariTamMi(metinler, isaretli));

  /*
    ================================================================
    DAVETIN CEVABI ARTIK OKUNUYOR — F-PPL-05b
    ================================================================
    Buradaki eski yorum "ikisi de KULLANILMIYOR: ekranin soyleyecegi
    sey her iki durumda da 'tamam, giris yapabilirsin'" diyordu ve
    o cumle YANLISTI: davet iki farkli sonucla bitebiliyor.

    18 yas alti + veli muvafakati yoksa sifre yaziliyor ama hesap
    `INVITED` kaliyor; o kullanici giris denediginde G-AUT-06 geregi
    "E-posta veya sifre hatali." aliyor — dogru sifresiyle.

    Sifre sifirlamada boyle bir ayrim yok, o yuzden orasi `null`
    donuyor ve ekran eski cumlesini koruyor.
  */
  const gonder = useMutation<DavetDurumu | null, unknown, void>({
    mutationFn: async () => {
      if (sayfa.akis === "davet")
        return (
          await davetiTamamla(sayfa.jeton, form.sifre, aktivasyonOnaylari(metinler, isaretli))
        ).durum;
      await sifreSifirla(sayfa.jeton, form.sifre);
      return null;
    },
    onSuccess: (durum) => {
      setMuvafakatBekliyor(durum === "MUVAFAKAT_BEKLIYOR");
      setBitti(true);
    },
    onError: (hata) => {
      // Aradan yeni metin sürümü yayınlandıysa ya da onay eksik kaldıysa: kutular sıfırlanır, güncel metin
      // yeniden okutulur (sürüm otomatik değiştirilip tekrar gönderilmez).
      if (hata instanceof ApiHatasi && AKTIVASYON_RIZA_KODLARI.includes(hata.kod)) {
        setIsaretli({});
        void metinSorgu.refetch();
      }
    },
  });

  /*
    HATA SUCLUYU GOSTERIYOR. Siniflandirma saf ve test edilir
    (`jetonHatasiniSinifla`); burada yalnizca `ApiHatasi`nin alanlari
    ona veriliyor. Ag kopmasi gibi `ApiHatasi` olmayan durumlar
    dogrudan `genel` — sunucunun soylemedigi bir sey ekranda
    soylenmiyor.
  */
  const gonderHatasi = gonder.error instanceof ApiHatasi ? gonder.error : null;
  // Onay metinleri okuması da jeton reddedebilir (süresi dolmuş / kullanılmış): aynı ölü bağlantı ekranı.
  const sorguHatasi = metinSorgu.error instanceof ApiHatasi ? metinSorgu.error : null;
  const sorguJetonHatasi =
    sorguHatasi !== null &&
    jetonHatasiniSinifla({ durum: sorguHatasi.durum, kod: sorguHatasi.kod }) === "jeton";
  const apiHatasi = sorguJetonHatasi ? sorguHatasi : gonderHatasi;
  const sinif = apiHatasi
    ? jetonHatasiniSinifla({
        durum: apiHatasi.durum,
        kod: apiHatasi.kod,
        ayrintiSifre: apiHatasi.ayrinti?.sifre,
      })
    : gonder.isError
      ? "genel"
      : null;

  const sifreEtiketi = sayfa.akis === "davet" ? "Şifre" : "Yeni şifre";

  /*
    BAGLANTI OLU: iki ayri yoldan gelinebiliyor ve EKRAN AYNI.
      1. jeton bicimsel olarak bozuk — sunucuya hic gidilmedi
      2. sunucu jetonu reddetti — suresi dolmus / kullanilmis
    Ikisini ayri ekran yapmak, ayni cikmazi iki kez cizmek olurdu.
  */
  if (!jetonTamam || sinif === "jeton") {
    return (
      <GirisKabugu
        baslik={jetonTamam ? "Bağlantı artık geçerli değil" : "Bağlantı geçersiz"}
        isaret={{ ikon: jetonTamam ? "bekliyor" : "uyari", ton: "uyari" }}
        aciklama={
          jetonTamam ? (
            /*
              SUNUCUNUN KENDI CUMLESI, uydurulmus bir ozet degil.
              `JetonService.MESAJ` uc neden icin (BULUNAMADI ·
              SURESI_GECMIS · KULLANILMIS) ayri ayri yazilmis. Kendi
              metnimizi yazsaydik ucunu tek cumleye indirger ve "bu
              baglanti daha once kullanilmis" bilgisini yok ederdik —
              oysa kullanicinin ne yapacagi tam da ona bagli.
            */
            apiHatasi?.message
          ) : (
            <>
              Bu bağlantı eksik ya da bozuk görünüyor. E-postadaki bağlantıyı tarayıcıya kopyalarken
              bir kısmı kesilmiş olabilir.
            </>
          )
        }
        altNot={
          <>
            {/*
              IKI SURE DE SOZLESMEDEN (`@koclukpanel/contracts`). Elle
              yazilmislardi ve ayni sinif hata bu projede GERCEKTEN
              oldu: sifremi unuttum ekrani "7 gün" diyordu, sunucudaki
              deger 30 dakikaydi.
            */}
            Davet bağlantıları <strong className="text-notr-800">{DAVET_GUN} gün</strong>, şifre
            sıfırlama bağlantıları{" "}
            <strong className="text-notr-800">{SIFRE_SIFIRLAMA_DAKIKA} dakika</strong> geçerlidir.
          </>
        }
      >
        <div>
          <NotKutusu ton="uyari">
            Bağlantı <strong>tek kullanımlıktır</strong>. Daha yeni bir istek gönderdiyseniz, bu
            eski bağlantı o yüzden de geçersiz olabilir.
          </NotKutusu>
        </div>

        {/*
          CIZIMDE BIRINCIL DUGME "Yeni bağlantı iste" — BURADA YOK ve
          sebebi olculdu: yeni baglanti istemek e-posta adresi
          gerektiriyor (`POST /auth/sifre-sifirlama-istegi`), bu ekran
          ise adresi BILMIYOR — jetonun kime ait oldugunu sunucu
          bilerek soylemiyor (G-AUT-06). Dugmeyi cizip kullaniciyi
          giris ekranina atmak, tuttugu sozu tutmayan bir dugme olurdu.

          Davet akisinda kullanici yeni baglantiyi kendisi zaten
          isteyemiyor: daveti yonetici gonderiyor. Metin o yuzden
          akisa gore degisiyor.
        */}
        <p className="text-notr-600 text-[13.5px] leading-[1.6]">
          {sayfa.akis === "davet" ? (
            <>Yeni bir davet bağlantısı için sizi davet eden yöneticiye başvurun.</>
          ) : (
            <>
              Giriş ekranındaki <strong className="text-notr-800">Şifremi unuttum</strong>{" "}
              bağlantısından yeni bir sıfırlama bağlantısı isteyebilirsiniz.
            </>
          )}
        </p>

        <Buton boyut="genis" onClick={() => (window.location.href = "/")}>
          Giriş ekranına dön
        </Buton>
      </GirisKabugu>
    );
  }

  if (bitti) {
    /*
      Muvafakat bekleyen hesapta ISARET DE DEGISIYOR: yesil tik
      "her sey tamam" diyor ve burada her sey tamam DEGIL. Bekleyen
      bir sey var ve tonu da onu soylemeli.
    */
    return (
      <GirisKabugu
        baslik={muvafakatBekliyor ? "Şifreniz kaydedildi" : "Tamam"}
        isaret={
          muvafakatBekliyor ? { ikon: "bekliyor", ton: "uyari" } : { ikon: "tamam", ton: "onay" }
        }
        aciklama={muvafakatBekliyor ? MUVAFAKAT_BEKLIYOR_METNI.bitti : metin.bitti}
        altNot={muvafakatBekliyor ? MUVAFAKAT_BEKLIYOR_METNI.altNot : metin.altNot}
      >
        {/*
          Otomatik giriş YAPILMIYOR: sunucu bu uçlarda oturum açmıyor
          ve `sifre-sifirla` mevcut çerezi bilerek temizliyor.
          Kullanıcıyı yeni şifresiyle bir kez giriş yaptırmak, şifreyi
          doğru yazdığını da doğruluyor.
        */}
        {/*
          MUVAFAKAT BEKLEYENDE "Giris ekranina git" YAZMIYOR: dugme
          kullaniciyi giremeyecegi bir ekrana gonderirdi ve orada
          "sifren yanlis" duvarina carpardi. Ikinci bir yol da yok —
          beklemek disinda yapabilecegi bir sey olmadigi icin dugme
          hic cizilmiyor.
        */}
        {!muvafakatBekliyor && (
          <Buton boyut="genis" onClick={() => (window.location.href = "/")}>
            Giriş ekranına git
          </Buton>
        )}
      </GirisKabugu>
    );
  }

  return (
    <GirisKabugu baslik={metin.baslik} aciklama={metin.aciklama} altNot={metin.altNot}>
      {sinif === "genel" && (
        <div>
          <NotKutusu ton="hata" duyur>
            {apiHatasi?.message ?? "İşlem tamamlanamadı."}
          </NotKutusu>
        </div>
      )}

      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          setDenendi(true);
          if (gecerli && onayHazir) gonder.mutate();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Alan
            etiket={sifreEtiketi}
            type="password"
            value={form.sifre}
            onChange={(e) => setForm((f) => ({ ...f, sifre: e.target.value }))}
            /*
              SUNUCUNUN SIFRE ITIRAZI ALANIN KENDI HATASI OLARAK
              gosteriliyor: `aria-describedby` ile alana bagli ve
              `role="alert"` ile duyuruluyor. Formun altinda ayri bir
              satir olsaydi, ekran okuyucu kullanicisi hangi alanin
              reddedildigini bulmak zorunda kalirdi.
            */
            hata={(denendi ? hatalar.sifre : undefined) ?? apiHatasi?.ayrinti?.sifre}
            autoComplete="new-password"
          />
          {/* Boş alanda hiç çizilmiyor — kararı bileşenin kendisi veriyor. */}
          <SifreGucu seviye={guc} ad={GUC_ADI[guc]} />
        </div>

        <Alan
          etiket={`${sifreEtiketi} (tekrar)`}
          type="password"
          value={form.tekrar}
          onChange={(e) => setForm((f) => ({ ...f, tekrar: e.target.value }))}
          hata={denendi ? hatalar.tekrar : undefined}
          autoComplete="new-password"
        />

        {/*
          KURAL KUTUSU — cizimden (`SifreSifirlama.dc.html`), ama
          IDDIASI KODA GORE DUZELTILDI. Cizim "sızmış şifre listesinde
          geçen" diyordu; `password.ts` sizmis parola servisine (HIBP
          gibi) hic sormuyor, YAYGIN SIFRE listesine bakiyor
          (`yaygin-sifreler.ts`, G-AUT-04). Ikisi ayni sey degil:
          "sizmis listesinde yok" demek, vermedigimiz bir guvence
          olurdu.

          Uzunluk `PERSONEL_EN_AZ` sabitinden — `minLengthFor`in
          personel degeriyle ayni. Ogrenci/veli icin sunucu 8 istiyor;
          cumle o yuzden "personel hesaplarinda" diyor.
        */}
        <div className="rounded-alan bg-notr-50 flex flex-col gap-1.5 px-[15px] py-[13px]">
          <span className="text-notr-800 text-[12.5px] font-semibold">Kural</span>
          <span className="text-notr-700 text-[12.5px] leading-[1.6]">
            Personel hesaplarında en az {PERSONEL_EN_AZ} karakter. Çok yaygın kullanılan bir şifre{" "}
            <strong>uzunluğu yeterli olsa da</strong> kabul edilmez.
          </span>
        </div>

        {davetMi && metinSorgu.isError && !sorguJetonHatasi && (
          <NotKutusu ton="hata" duyur>
            Onay metinleri alınamadı. Bağlantınızı kontrol edip{" "}
            <button type="button" className="underline" onClick={() => void metinSorgu.refetch()}>
              tekrar deneyin
            </button>
            .
          </NotKutusu>
        )}
        {metinler.length > 0 && (
          <fieldset className="flex flex-col gap-2">
            <legend className="text-notr-800 text-[12.5px] font-semibold">
              Hesabı açmak için onaylayın
            </legend>
            {metinler.map((m) => (
              <div key={m.tur} className="flex flex-col gap-1">
                <OnayKutusu
                  checked={isaretli[onayAnahtari(m)] === true}
                  onChange={(e) =>
                    setIsaretli((x) => ({ ...x, [onayAnahtari(m)]: e.target.checked }))
                  }
                  etiket={
                    <>
                      <strong>{m.baslik}</strong> metnini okudum, onaylıyorum.
                    </>
                  }
                />
                <details className="pl-[26px]">
                  <summary className="text-notr-600 cursor-pointer text-xs">
                    Metni oku (sürüm {m.surum})
                  </summary>
                  <p className="text-notr-700 mt-1 text-xs leading-[1.6] whitespace-pre-wrap">
                    {m.govde}
                  </p>
                </details>
              </div>
            ))}
          </fieldset>
        )}

        <Buton
          type="submit"
          boyut="genis"
          disabled={!onayHazir}
          yukleniyor={gonder.isPending}
          yukleniyorMetni="Gönderiliyor"
        >
          {metin.dugme}
        </Buton>
      </form>
    </GirisKabugu>
  );
}
